import {
  put,
  call,
  takeLatest,
  select,
  debounce,
  takeEvery,
  all,
} from 'redux-saga/effects';

import { replace } from 'connected-react-router';
import _assign from 'lodash/assign';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _map from 'lodash/map';
import _has from 'lodash/has';
import _cloneDeep from 'lodash/cloneDeep';
import _isArray from 'lodash/isArray';
import _isString from 'lodash/isString';
import _lowerCase from 'lodash/lowerCase';
import ActionTypes from '../constants/ActionTypes';
import { progressStart, progressStop } from '../actions';
import {
  getTestBySlug,
  startTest,
  initTest,
  getTestSolutionSetByTestId,
  fetchNextProblem as fetchNextProblemApi,
  getProblemSolution,
  // getProblemsBySlug,
  getInvite,
  clearAnswer,
  continueTest,
  getFeedbackApi,
  uploadSubjectiveSolutionAttachment,
  prepareUrlForIframe,
  getAllRevisions,
} from '../api/test';
import { createProctorV2Settings } from '../api/proctor';
import {
  getAllSolutionRevisions,
  getSolutionSet,
  setSaveInProgress,
  updateTestExpiryAndSessionState,
} from '../actions/test';
import { updateAudioUploadStatus } from '../actions/audioProblem';
import {
  enableWebcam,
  hideSystemCheck,
  setProctorBatchABEnabled,
  setProctoringABEnabled,
  showSystemCheck,
  updateProctoringSignedUrls,
} from '../actions/proctor';
import { trackTestStart } from '../actions/eventTracking';
import { externalProxyUrl } from '../constants/AppConstants';
import { showErrorToast } from '../actions/toast';
import {
  httpStatusCodes,
  NETWORK_ERROR,
  SAVE_IN_PROGRESS,
  httpMethods,
} from '../constants/Http';
import { problemTypes } from '../constants/ProblemTypes';
import { extractPathFromProxy, getUrlParams } from '../utils/http';
import {
  preparePlaygroundSolutionAndSave,
  solutionIdMapsToProblemSlug,
  prepareSolutionAndSave,
} from './utils';
import { captureException } from '../utils/sentry';
import { getApiErrorData } from '../utils/helperFunction';
import { errorLogger, proctoringUBALogger } from '../utils/trackingUtil';
import {
  IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_MODE,
  PROCTOR_SIGNED_URL_DEFAULT_IMAGE_BATCH_SIZE,
  PROCTOR_SIGNED_URL_DEFAULT_VIDEO_BATCH_SIZE,
  PROCTOR_SIGNED_URL_INITIAL_BATCH_SIZE,
  PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_RETRY,
  WEBCAM_ERROR_DURATION_LOCALSTORAGE_KEY,
  WEBCAM_ERROR_START_TIME_LOCALSTORAGE_KEY,
  SYSTEM_CHECK_SESSION_STORAGE_KEY,
} from '../constants/Proctor';
import { runCodeWithTestCases } from '../api/coding';
import {
  PREDICTION_FILE,
  REVISION_CREATED,
  REVISION_ERROR,
} from '../components/ProblemTypes/FileBasedEvaluation/constants';
import {
  hidePreparingAssessmentModal,
  showPreparingAssessmentModal,
} from '../actions/appState';
import { SESSION_STATE } from '../constants/AssessmentConstants';
import { WEBSOCKET_EVENT_SEQUENCE_SESSIONSTORAGE_DEFAULT_VALUE } from '../constants/Socket';
import {
  getWebsocketSequenceStorageKey,
  getFileBasedPayloadSchema,
  getSectionTotalProblems,
} from '../utils/utils';
import {
  TEST_TYPE,
  TEST_TYPE_SESSION_STORAGE_KEY,
} from '../constants/Constants';
import { EAudioRecordingStatus } from '../components/ProblemTypes/Audio/constants';
import { renderTemplate } from '../utils/stringUtils';

function* getTest(args) {
  const isTestSubmitInProcess = yield select(
    state => state.test.isTestSubmitInProcess
  );
  try {
    if (!isTestSubmitInProcess) yield put(progressStart());
    const resp = yield call(getTestBySlug, args.slug);
    if (
      sessionStorage.getItem(TEST_TYPE_SESSION_STORAGE_KEY) === TEST_TYPE.QUIZ
    ) {
      resp.data.test_type_verbose = TEST_TYPE.QUIZ;
    }
    /**
     * Custom forms for old tests are not coming as array of objects. They come
     * as array of strings. Need to manually check them and convert them into array
     * of objects as input fields
     */
    const customForm = _get(
      resp,
      'data.settings.hacker_extra_data.custom_data.enabled'
    );
    if (customForm) {
      const fields = _get(
        resp,
        'data.settings.hacker_extra_data.custom_data.fields'
      );
      if (_isArray(fields) && fields[0] && _isString(fields[0])) {
        _set(
          resp,
          'data.settings.hacker_extra_data.custom_data.fields',
          _map(fields, val => ({
            label: val,
            required: false,
            type: 'input',
          }))
        );
      }
    }
    resp.data.productType = 'Test';
    resp.data.productTypeSmall = 'test';
    resp.data.isAiInterview = false;
    if (resp.data.is_test_created_for_phase) {
      resp.data.productType = 'Contest';
      resp.data.productTypeSmall = 'contest';
    }
    if (resp.data.sub_test_type === 'INT') {
      resp.data.productType = 'Interview';
      resp.data.productTypeSmall = 'interview';
      resp.data.isAiInterview = true;
    }
    yield put({
      type: `${ActionTypes.GET_TEST}_SUCCESS`,
      payload: resp.data,
    });
    if (!isTestSubmitInProcess) yield put(progressStop());
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'GTD');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (!isTestSubmitInProcess) yield put(progressStop());
  }
}

function* initializeTest(args) {
  const { showChecksModal, isInfraAllocated, rethrowOnError } = args;
  if (showChecksModal) {
    yield put(showPreparingAssessmentModal());
  }
  try {
    const test = yield select(state => state.test);
    const socket = yield select(state => state.socket);
    const proctorEnabled = _get(test, 'settings.proctor.enabled');
    const webcamEnabled = _get(test, 'settings.proctor.snapshot');
    const videoProctoringEnabled = _get(test, 'settings.proctor.video.enabled');
    const idProofAuthEnabled =
      test?.settings?.hacker_extra_data?.identity_card?.enabled;
    // logging test setting
    proctoringUBALogger(
      'info',
      `proctoring : ${proctorEnabled}, image-proctoring: ${webcamEnabled}, video-proctoring: ${videoProctoringEnabled}, idProofAuthEnabled: ${idProofAuthEnabled}`
    );
    const resp = yield call(
      initTest,
      args.userId,
      args.userType,
      args.testId,
      args.testSlug
    );
    // initializing the variables with default value if not defined
    const {
      assessmentId = null,
      assessmentType = null,
      isExtendedTest = false,
      websocketEventSequence = false,
    } = resp;
    const websocketSequenceKey = getWebsocketSequenceStorageKey(assessmentId);
    const cachedWebsocketEventSequence = sessionStorage.getItem(
      websocketSequenceKey
    );
    const websocketEventSeq =
      websocketEventSequence > 0 && !cachedWebsocketEventSequence
        ? websocketEventSequence
        : WEBSOCKET_EVENT_SEQUENCE_SESSIONSTORAGE_DEFAULT_VALUE;
    sessionStorage.setItem(websocketSequenceKey, websocketEventSeq);
    // enviornment setup call for checking djp problem in test and  creating pod

    yield put({
      type: ActionTypes.CHECK_DJP_PROBLEM_IN_TEST_AND_SETUP,
      testSolutionId: assessmentId,
    });
    if (!isInfraAllocated) {
      yield put({
        type: ActionTypes.CHECK_INFRA_ALLOCATION,
        payload: {
          assessmentId,
          isExtendedTest,
        },
      });
    }

    const proctoringSystemSettings = test?.system_settings?.proctoring;
    const proctoringABEnabled = proctoringSystemSettings?.ab;
    let isProctoringABEnabled = false;
    if (proctoringABEnabled) {
      const proctoringABPercentage = proctoringSystemSettings?.percentage;
      isProctoringABEnabled =
        proctoringABPercentage > 0 && proctoringABPercentage !== 100
          ? Math.floor(assessmentId % (100 / proctoringABPercentage)) === 0
          : proctoringABEnabled;
      yield put(setProctoringABEnabled(isProctoringABEnabled));
    }

    // proctor batch ab
    const proctoringBatchSystemSettings =
      test?.system_settings?.batch_proctoring_scaling;
    const proctoringBatchABEnabled = proctoringBatchSystemSettings?.ab;
    let isProctoringBatchABEnabled = false;
    if (proctoringBatchABEnabled) {
      const proctoringABPercentage = proctoringBatchSystemSettings?.percentage;
      isProctoringBatchABEnabled =
        proctoringABPercentage > 0 && proctoringABPercentage !== 100
          ? Math.floor(assessmentId % (100 / proctoringABPercentage)) === 0
          : proctoringBatchABEnabled;
      yield put(setProctorBatchABEnabled(isProctoringBatchABEnabled));
    }

    if (
      (idProofAuthEnabled || proctorEnabled) &&
      assessmentId &&
      assessmentType
    ) {
      try {
        const response = yield call(createProctorV2Settings, {
          assessmentId,
          assessmentType,
          isProctoringABEnabled,
        });
        // Resume logic: strict camera status may return accumulated webcam-error duration (seconds).
        // Only hydrate duration when strict_camera_status is enabled.
        const strictCam = response?.data?.data?.strict_camera_status;
        const strictCamEnabled = Boolean(strictCam?.enabled);
        const webcamErrorDurationFromApi = strictCamEnabled
          ? strictCam?.duration
          : undefined;
        if (webcamErrorDurationFromApi) {
          try {
            localStorage.setItem(
              WEBCAM_ERROR_DURATION_LOCALSTORAGE_KEY,
              String(webcamErrorDurationFromApi)
            );
            localStorage.setItem(WEBCAM_ERROR_START_TIME_LOCALSTORAGE_KEY, '');
          } catch (e) {
            // non-fatal (storage may be blocked)
          }
        }
        const nextImageSequence = response?.data?.data?.image_sequence;
        let cachedSignedUrls = localStorage.getItem(
          PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY
        );
        let proctoringSignedUrlsLength = 0;
        if (cachedSignedUrls) {
          cachedSignedUrls = JSON.parse(cachedSignedUrls);
          proctoringSignedUrlsLength =
            Object.keys(cachedSignedUrls)?.length || 0;
        }
        if (nextImageSequence > 1) {
          localStorage.setItem(
            IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY,
            nextImageSequence
          );
          localStorage.setItem(
            PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
            nextImageSequence
          );
        } else if (webcamEnabled || videoProctoringEnabled) {
          if (proctoringSignedUrlsLength === 0 && isProctoringBatchABEnabled) {
            const tempToken = response?.data?.token;
            yield put(
              updateProctoringSignedUrls(
                {
                  token: tempToken,
                  assessmentId,
                  mode: videoProctoringEnabled
                    ? PROCTOR_MODE.VIDEO
                    : PROCTOR_MODE.IMAGE,
                  batchSize: PROCTOR_SIGNED_URL_INITIAL_BATCH_SIZE,
                  lastSignedUrl: undefined,
                  nextImageSequence: 1,
                  initial_call: test.session_state !== 'ACTIVE' || undefined,
                },
                PROCTOR_SIGNED_URL_RETRY
              )
            );
          }
        }
      } catch (createProctorV2SettingsError) {
        const { toast } = yield select(state => state.appStatus);
        const errorInfo = getApiErrorData(createProctorV2SettingsError, 'PST');
        if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
          yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
        }
      }
    }

    // init test success call sequence is being change to make sure
    // identity image checking call is triggered post setting call
    yield put({
      type: `${ActionTypes.INIT_TEST}_SUCCESS`,
      payload: resp,
    });

    yield put(setProctoringABEnabled(isProctoringABEnabled));
    yield put(setProctorBatchABEnabled(isProctoringBatchABEnabled));

    const batchSize = videoProctoringEnabled
      ? proctoringSystemSettings?.batch_count_video ||
        PROCTOR_SIGNED_URL_DEFAULT_VIDEO_BATCH_SIZE
      : proctoringSystemSettings?.batch_count_image ||
        PROCTOR_SIGNED_URL_DEFAULT_IMAGE_BATCH_SIZE;

    yield put({
      type: ActionTypes.UPDATE_SIGNED_URL_BATCH_SIZE,
      batchSize,
    });

    yield put({
      type: ActionTypes.SET_REALTIME_PROCTORING_FEEDBACK_ENABLED,
      payload: proctoringSystemSettings?.feedback,
    });

    if (socket && !socket.socketActive && assessmentId && assessmentType) {
      yield put({
        type: ActionTypes.SOCKET_FETCH_SETTINGS,
        payload: {
          assessmentId,
          assessmentType,
        },
      });

      yield put({
        type: ActionTypes.INITIATE_WEBSOCKET_CONNECTION,
      });
      if (showChecksModal && (webcamEnabled || videoProctoringEnabled)) {
        yield put(enableWebcam());
      }
    }
    /**
     * Enable webcam for candidate id auth when no proctoring or basic proctoring enabled.
     */
    if (
      showChecksModal &&
      idProofAuthEnabled &&
      (!proctorEnabled ||
        (proctorEnabled && !(webcamEnabled || videoProctoringEnabled)))
    ) {
      yield put(enableWebcam());
    }
    if (showChecksModal) {
      yield put(showSystemCheck());
    }
  } catch (e) {
    // sample test, need to revisit
    const { toast } = yield select(state => state.appStatus);
    let errorInfo = {};
    if (e?.config?.url?.includes('solutionset?context=test')) {
      errorInfo = getApiErrorData(e, 'SS1');
    }
    if (e?.config?.url?.includes('test.init')) {
      errorInfo = getApiErrorData(e, 'TIN');
    }
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    // Toast is already shown above; only rethrow when a caller (e.g.
    // `initializeAndStartTest`) explicitly needs to know the outcome. The
    // primary INIT_TEST dispatch path doesn't pass this, so `takeLatest`
    // below never sees a rejection.
    if (rethrowOnError) {
      throw e;
    }
  } finally {
    if (showChecksModal) {
      yield put(hidePreparingAssessmentModal());
    }
  }
}

function* startTakingTest(args) {
  try {
    yield put(progressStart());
    // clearing offline data info from session storage
    sessionStorage.removeItem('offline-duration-in-seconds');
    const { isProctorBatchABEnabled } = yield select(state => state.proctor);
    yield put({
      type: ActionTypes.CLEAR_LOCAL_DATA,
      isTestStart: isProctorBatchABEnabled,
    });
    const resp = yield call(startTest, args.testId);
    yield put({
      type: `${ActionTypes.START_TEST}_SUCCESS`,
      payload: resp.data,
    });
    yield put(
      trackTestStart({
        test_slug: args.testSlug,
      })
    );
    yield put({
      type: ActionTypes.GET_TEST,
      slug: args.testSlug,
    });
    yield put(hideSystemCheck());
    const { subTestType } = yield select(state => ({
      subTestType: state.test.sub_test_type,
    }));
    yield put(
      subTestType === 'INT'
        ? replace(`/test/${args.testSlug}/aiinterview`)
        : replace(`/test/${args.testSlug}/problems`)
    );
  } catch (e) {
    const { toast, productType } = yield select(state => ({
      toast: state.appStatus.toast,
      productType: state.test.productType,
    }));
    const errorInfo = getApiErrorData(e, 'STT');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      errorInfo.message = renderTemplate(errorInfo.message, { productType });
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    // See the matching comment in `initializeTest` above.
    if (args.rethrowOnError) {
      throw e;
    }
  } finally {
    yield put(progressStop());
  }
}

function* continueTakingTest(args) {
  try {
    yield put(progressStart());
    const resp = yield call(continueTest, args.testSlug, args.inviteId);
    yield put({
      type: `${ActionTypes.CONTINUE_TEST}_SUCCESS`,
      payload: resp.data,
      testId: args.testId,
    });
    yield put(getSolutionSet(args.testId, true));
    yield put(hideSystemCheck());
    const { subTestType } = yield select(state => ({
      subTestType: state.test.sub_test_type,
    }));
    yield put(
      subTestType === 'INT'
        ? replace(`/test/${args.testSlug}/aiinterview`)
        : replace(`/test/${args.testSlug}/problems`)
    );
  } catch (e) {
    const { toast, productType } = yield select(state => ({
      toast: state.appStatus.toast,
      productType: state.test.productType,
    }));
    const errorInfo = getApiErrorData(e, 'RST');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      errorInfo.message = renderTemplate(errorInfo.message, { productType });
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

function* getUserInvite(args) {
  try {
    yield put(progressStart());
    const resp = yield call(getInvite, args.inviteId);
    yield put({
      type: `${ActionTypes.GET_INVITE}_SUCCESS`,
      payload: resp.data,
    });
    yield put(progressStop());
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'INV');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    yield put(progressStop());
  }
}

/* 
  Todo: make function name `getSolution` specific.
    1. For MCQ problem type it should be `getMCQSolution`
    2. For Subjective problem type it should be `getSubjectiveSolution`
  Move these to seperate file and abstract the base logic in common saga.
*/

function* getSolution(args) {
  try {
    const auth = yield select(state => state.auth.initUserDetails);
    const test = yield select(state => state.test);

    /**
     * Check if solution is present for the problem and then make the API call
     * to fetch the solution
     */
    const sections = yield select(state => state.sections.sectionsNotGrouped);
    let isSolutionPresent = false;
    let solutionSlug;
    sections.forEach(section => {
      if (_isArray(section.problems)) {
        section.problems.forEach(problem => {
          if (
            !_isEmpty(problem.solution) &&
            problem.slug === args.problemSlug
          ) {
            isSolutionPresent = true;
            solutionSlug = problem.solution.slug;
          }
        });
      }
    });
    if (!isSolutionPresent) {
      return;
    }
    const resp = yield call(
      getProblemSolution,
      auth.user_id,
      auth.usertype,
      test.slug,
      args.problemSlug,
      'test',
      solutionSlug
    );
    /*
      If user attempts any MCQ question refresh the page
      and fires claer solution then infinite solution
      get request will be fired response status 404.

      Handle:
        HTTP 200 OK: Call GET_PROBLEM_SOLUTION_SUCCESS
    */
    if (resp.status === httpStatusCodes.OK) {
      yield put({
        type: `${ActionTypes.GET_PROBLEM_SOLUTION}_SUCCESS`,
        payload: resp.data,
      });
    }
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'GUS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  }
}

function* getdesmondSolution(args) {
  try {
    const auth = yield select(state => state.auth.initUserDetails);
    const test = yield select(state => state.test);

    const resp = yield call(
      getProblemSolution,
      auth.user_id,
      auth.usertype,
      test.slug,
      args.problemSlug
    );
    if (resp.status === httpStatusCodes.OK) {
      yield put({
        type: `${ActionTypes.GET_PROBLEM_SOLUTION}_SUCCESS`,
        payload: resp.data,
      });
    }
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'GUS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  }
}
function* getTestSolutionSet(args) {
  // const test = yield select(state => state.test);
  try {
    const testSolutionSet = yield select(state => state.testSolutionSet);
    if (!_isEmpty(testSolutionSet) && !args.isForced) {
      return;
    }
    yield put(progressStart());
    const test = yield select(state => state.test);
    const subTestType = test?.sub_test_type === 'ADP' ? 'ADP' : undefined;
    const resp = yield call(
      getTestSolutionSetByTestId,
      args.testId,
      subTestType
    );
    yield put({
      type: `${ActionTypes.GET_TEST_SOLUTIONSET}_SUCCESS`,
      payload: resp.data,
    });
  } catch (e) {
    const { toast, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(e, 'SS2');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (e.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    // const message = _get(e, 'response.data', null);
    // if (
    //   test.is_expired === true &&
    //   message === 'Your test session has ended.'
    // ) {
    //   yield put(replace(`/test/${test.slug}/feedback`));
    // } else {
    //   yield put(replace(`/test/${test.slug}`));
    // }
  } finally {
    yield put(progressStop());
  }
}

/* 
  Todo: make function name `saveOrUpdateSolution` specific.
    1. For MCQ problem type it should be `createOrUpdateMCQSolution`
    2. For Subjective problem type it should be `createOrUpdateSubjectiveSolution`
  Move these to seperate file and abstract the base logic in common saga.
*/

function* saveOrUpdateSolution(args) {
  try {
    yield call(prepareSolutionAndSave, args);
  } catch (error) {
    /*
      MCQ clear failed due to NETWORK disconnect.
      Backend DELETED that solution object.
      When NETWORK connects back and user
      clicks on any mcq option. 
      Handle:
        1. HTTP STATUS 404, HTTP METHOD PATCH: update's the mcq option.
        2. Re-create this solution.
        3. NETWORK ERROR: User change MCQ option and they went offline.
      TODO: Handle other backend responses
    */
    if (error.response) {
      const { response } = error;
      if (
        response.status === httpStatusCodes.NOT_FOUND &&
        response.config.method === httpMethods.PATCH
      ) {
        const path = extractPathFromProxy(
          new URL(response.config.url).pathname
        );
        const urlParams = getUrlParams(path, '/api/v1/solution/:solutionSlug');
        // fetching solution object if exists else false.
        const solution = yield select(({ solutions }) => {
          return (
            !_isEmpty(solutions) &&
            _has(solutions, args.problemSlug) &&
            _get(solutions, args.problemSlug)
          );
        });
        if (
          solution &&
          solution.slug === urlParams.solutionSlug &&
          solution.solution_type === problemTypes.MCQ
        ) {
          // Clear MCQ solution from the state
          yield put({
            type: `${ActionTypes.CLEAR_SOLUTION}_SUCCESS`,
            problemSlug: args.problemSlug,
          });
          // Re-create the solution
          yield put({
            type: ActionTypes.SAVE_SOLUTION_NO_DEBOUNCE,
            problemSlug: args.problemSlug,
            solution: {
              problem: `/api/v1/problem/${args.problemSlug}`,
              solution_type: problemTypes.MCQ,
              choice: args.solution.choice,
            },
          });
          return;
        }
      }
    }
    const { toast, isTestSubmitted, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(error, 'UPS');
    if (
      errorInfo &&
      errorInfo.message &&
      !toast?.apiErrorInfo &&
      !args.isAutoSave &&
      !isTestSubmitted
    ) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (error.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      captureException(error);
  }
}

function* saveOrUpdatePlaygroundSolution(args) {
  try {
    yield call(preparePlaygroundSolutionAndSave, args);
  } catch (error) {
    errorLogger(error);
  }
}
function* getBookmarks() {
  try {
    const test = yield select(state => state.test);
    const bookmark = localStorage.getItem(`${test.slug}.bookmarks`);
    const marks = bookmark ? JSON.parse(bookmark) : {};
    yield put({
      type: `${ActionTypes.GET_BOOKMARKS}_SUCCESS`,
      payload: marks,
    });
  } finally {
    // to do
  }
}
function* setBookmark(args) {
  try {
    const test = yield select(state => state.test);
    const { problemSlug, status } = args;
    let curr = localStorage.getItem(`${test.slug}.bookmarks`);
    curr = _isEmpty(curr) ? {} : JSON.parse(curr);
    curr[problemSlug] = status ? true : undefined;
    localStorage.setItem(`${test.slug}.bookmarks`, JSON.stringify(curr));
    yield put({
      type: `${ActionTypes.SET_BOOKMARK}_SUCCESS`,
      payload: curr,
    });
  } finally {
    // to do
  }
}
function* setAttemptsCount(args) {
  try {
    if (_isString(args.problem)) {
      const attemptedProblems = yield select(
        state => state.appStatus.attemptedProblems
      );
      const newAttemptedProblems = _assign({}, attemptedProblems, {
        [args.problem]: true,
      });
      yield put({
        type: `${ActionTypes.SET_ATTEMPTS_COUNT}_SUCCESS`,
        problems: newAttemptedProblems,
      });
    }
  } finally {
    // to do
  }
}

function* setClusterAttemptsCount(args) {
  try {
    const { indexedProblems: getProblemIndexList, cached } = yield select(
      state => state.problems
    );
    const getProblemsList = cached[0].problems;
    const getSolutionsList = yield select(state => state.solutions);
    const curIndex = getProblemIndexList[args.problem];
    const curProblem = getProblemsList[curIndex - 1];
    const curSolnLength = Object.keys(getSolutionsList[args.problem] || {})
      .length;
    if (
      typeof args.problem === 'string' &&
      curProblem.children &&
      (curProblem.children.length || 0) - 1 === curSolnLength
    ) {
      const attemptedProblems = yield select(
        state => state.appStatus.attemptedProblems
      );
      const newAttemptedProblems = {
        ...attemptedProblems,
        [args.problem]: true,
      };
      yield put({
        type: `${ActionTypes.SET_ATTEMPTS_COUNT}_SUCCESS`,
        problems: newAttemptedProblems,
      });
    }
  } finally {
    // to do
  }
}

function* getFeedback() {
  try {
    yield put(progressStart());
    const test = yield select(state => state.test);
    yield call(getFeedbackApi, test.slug);
    yield put({
      type: `${ActionTypes.GET_FEEDBACK}_SUCCESS`,
      payload: { feedback: true },
    });
  } catch (e) {
    yield put({
      type: `${ActionTypes.GET_FEEDBACK}_FAIL`,
    });
  } finally {
    yield put(progressStop());
  }
}

/*
  Todo: We need to delete this function and put all logic in one single function
  'saveSubjectiveSolution'
 */
function* uploadProblemAttachment(args) {
  try {
    const { solution_type } = args.solution;

    yield put(progressStart());
    if (args.file?.name) {
      const payload = new FormData();
      payload.append('file', args.file);
      payload.append('upload_type', 'generic');
      payload.append('filename_prefix', '');
      payload.append('name', args.file.name);

      const res = yield call(uploadSubjectiveSolutionAttachment, payload);
      const url = _get(res.data, 'resource_uri');

      const solution = _cloneDeep(args.solution);

      if (solution_type === problemTypes.FBE) {
        const { fieldName, fileBasedData } = args.solution;

        const fileBasedArgs = {
          fieldName,
          fileBasedData,
          url,
        };
        solution.file_based_data = getFileBasedPayloadSchema(fileBasedArgs);
      } else if (_isArray(solution.attachments)) {
        solution.attachments.push(url);
      } else {
        _set(solution, 'attachements', [url]);
      }

      yield call(prepareSolutionAndSave, {
        problemSlug: args.problemSlug,
        slug: args.solutionSlug,
        solution,
        retryCallback: args.retryCallback,
      });
    }
  } catch (error) {
    const { toast } = yield select(state => state.appStatus);
    let errorInfo = {};
    if (error?.config?.url?.includes('/upload')) {
      errorInfo = getApiErrorData(error, 'FUP');
      if (error.message !== NETWORK_ERROR) captureException(error);
    } else if (error.message === NETWORK_ERROR) {
      yield put(
        showErrorToast(
          `Could not upload attachment due to ${_lowerCase(NETWORK_ERROR)}`
        )
      );
    } else {
      yield put(
        showErrorToast(
          `Something went wrong. It's not you, It's us. Please upload file again.`
        )
      );
      captureException(error);
    }

    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

function* checkUrlForIframe(args) {
  try {
    const imageEx = /(png|jpg|jpeg|svg|bmp|webp|gif)/;
    const ext = args.url.split('.').pop();
    if (ext.match(imageEx)) {
      yield put({
        type: `${ActionTypes.CHECK_URL_FOR_IFRAME}_SUCCESS`,
        url: args.url,
      });
      return;
    }
    const url = `${externalProxyUrl}${args.url}`;
    const checkContentType = /(text\/html|application\/pdf)/;
    const resp = yield call(prepareUrlForIframe, url);
    const headerContentType = resp.headers['content-type'];
    if (headerContentType && headerContentType.match(checkContentType)) {
      yield put({
        type: `${ActionTypes.CHECK_URL_FOR_IFRAME}_SUCCESS`,
        url,
      });
    } else {
      yield put({
        type: `${ActionTypes.CHECK_URL_FOR_IFRAME}_FAIL`,
        url,
      });
    }
  } catch (e) {
    const url = `${externalProxyUrl}${args.url}`;
    yield put({
      type: `${ActionTypes.CHECK_URL_FOR_IFRAME}_FAIL`,
      url,
    });
  }
}

function* clearSolution(args) {
  try {
    const { clusterSlug } = args;
    yield put(setSaveInProgress(args.problemSlug, true));
    const { testSlug, subTestType } = yield select(state => ({
      testSlug: state.test.slug,
      subTestType: state.test.sub_test_type,
    }));
    const clearResp = yield call(
      clearAnswer,
      args.slug,
      testSlug,
      subTestType === 'ADP' ? subTestType : undefined
    );
    /*
      Handling:
        1. HTTP 204: backend successfully DELETE's the request
      TODO: Handle other backend responses
    */
    if (httpStatusCodes.NO_CONTENT === clearResp.status) {
      yield put({
        type: `${ActionTypes.REMOVE_PROBLEM_SOLUTION}`,
        problemSlug: args.problemSlug,
        clusterSlug,
      });
      yield put({
        type: `${ActionTypes.CLEAR_SOLUTION}_SUCCESS`,
        problemSlug: args.problemSlug,
        clusterSlug,
      });
    }
  } catch (error) {
    const { toast, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(error, 'DPS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (error.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    if (error.message !== NETWORK_ERROR) captureException(error);

    /*
      Handling:
        1. HTTP 404: backend could not find the solution related to that solution slug. This can happen if the multiple DELETE's were fired or user fires the clear and they went offline.
        2. NETWORK ERROR: User fires the clear and they went offline.
      TODO: Handle other backend responses
    */
    if (error.response) {
      const { response } = error;
      if (
        response.config.method === httpMethods.DELETE &&
        response.status === httpStatusCodes.NOT_FOUND
      ) {
        const path = extractPathFromProxy(
          new URL(response.config.url).pathname
        );
        const urlParams = getUrlParams(path, '/api/v1/solution/:solutionSlug');
        const solution = yield select(({ solutions }) => {
          return (
            !_isEmpty(solutions) &&
            _has(solutions, args.problemSlug) &&
            _get(solutions, args.problemSlug)
          );
        });
        if (solution && solution.slug === urlParams.solutionSlug)
          yield put({
            type: `${ActionTypes.CLEAR_SOLUTION}_SUCCESS`,
            problemSlug: args.problemSlug,
          });
      }
      return;
    }
  } finally {
    yield put(setSaveInProgress(args.problemSlug, false));
  }
}

function* testCaseRunWrapper(args) {
  try {
    const { auth, test } = yield select(state => ({
      auth: state.auth.initUserDetails,
      test: state.test,
    }));
    const tempData = args.data ? { ...args.data } : {};

    const { problemType, problemSlug, solutionSlug } = yield call(
      solutionIdMapsToProblemSlug,
      tempData
    );

    if (problemType === problemTypes.FBE) {
      const resp = yield call(
        getProblemSolution,
        auth.user_id,
        auth.usertype,
        test.slug,
        problemSlug
      );
      yield put({
        type: `${ActionTypes.GET_PROBLEM_SOLUTION}_SUCCESS`,
        payload: resp.data,
      });

      if (tempData.status === REVISION_CREATED) {
        yield put(getAllSolutionRevisions(problemSlug, solutionSlug));
      }
    }
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo =
      e.message === REVISION_ERROR
        ? getApiErrorData(e, 'URH')
        : getApiErrorData(e, 'GUS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  }
}

function* getSolutionRevisions(args) {
  try {
    const { solutionSlug, problemSlug } = args;
    const allRevisions = yield call(getAllRevisions, solutionSlug);
    yield put({
      type: `${ActionTypes.GET_ALL_REVISIONS}_SUCCESS`,
      payload: { data: allRevisions.data, problemSlug },
    });
  } catch {
    const revisionError = new Error(REVISION_ERROR);
    throw revisionError;
  }
}

function* onSubmitAndEvaluateFbe(args) {
  const { solutionId, solutionMeta, retryCallback } = args.payload;
  const {
    fileInfo: file,
    problemSlug,
    solutionSlug,
    fileBasedData,
  } = solutionMeta;

  yield call(uploadProblemAttachment, {
    type: `${ActionTypes.UPLOAD_ATTACHMENT_PROB}`,
    file,
    solution: {
      problem: solutionMeta.problem,
      solution_type: problemTypes.FBE,
      fieldName: PREDICTION_FILE,
      fileBasedData: fileBasedData || [],
    },
    problemSlug,
    solutionSlug,
    retryCallback,
  });

  const { socket, allSolutions } = yield select(state => ({
    socket: state.socket,
    allSolutions: state.solutions,
  }));

  yield call(runCodeWithTestCases, {
    solution_id: solutionId || allSolutions[problemSlug].id,
    sockmeta: {
      connid: socket.connid,
      wid: socket.wid,
      sockid: socket.sockid,
    },
  });
}

function* saveOrUpdateVideoSolution(args) {
  try {
    yield call(prepareSolutionAndSave, args);
  } catch {
    yield put(setSaveInProgress(args.problemSlug, false));
  }
}

function* saveOrUpdateAudioSolution(args) {
  try {
    yield call(prepareSolutionAndSave, args);
  } catch (err) {
    errorLogger(err, { problemSlug: args.problemSlug });
    yield all(
      put(setSaveInProgress(args.problemSlug, false)),
      put(
        updateAudioUploadStatus(args.problemSlug, EAudioRecordingStatus.FAILED)
      )
    );
  }
}

function* adaptiveNextProblemSaga(args) {
  try {
    const { problemSlug } = args;
    const { test, testSolutionSet, solutions, cached } = yield select(
      state => ({
        test: state.test,
        testSolutionSet: state.testSolutionSet,
        solutions: state.solutions,
        cached: state.problems?.cached,
      })
    );

    if (!problemSlug || test?.sub_test_type !== 'ADP') return;

    const solutionSetSections = Array.isArray(testSolutionSet?.sections)
      ? testSolutionSet.sections
      : [];
    const currentTimedSection = solutionSetSections.find(
      s => s?.status === 'CTK'
    );
    if (!currentTimedSection) return;

    const getSectionShownCount = section => {
      const arr = section?.problem_ids || section?.problems || [];
      return Array.isArray(arr) ? arr.length : 0;
    };

    const getProblemIdFromCache = (cachedGroups, slug) => {
      const groups = Array.isArray(cachedGroups) ? cachedGroups : [];
      const groupWithProblem = groups.find(
        grp =>
          Array.isArray(grp?.problems) &&
          grp.problems.some(p => p?.slug === slug && p?.id)
      );
      const match = Array.isArray(groupWithProblem?.problems)
        ? groupWithProblem.problems.find(p => p?.slug === slug && p?.id)
        : null;
      return match?.id || null;
    };

    const currentProblemId =
      currentTimedSection?.current_problem_id ||
      getProblemIdFromCache(cached, problemSlug);

    if (!testSolutionSet?.id || !currentProblemId) {
      yield put(replace(`/test/${test.slug}/problems`));
      return;
    }

    // Save solution first (backend uses this to compute the next adaptive problem)

    const existingSolution = solutions?.[problemSlug] || {};
    if (existingSolution?.choice) {
      const solutionPayload = {
        problem: `/api/v1/problem/${problemSlug}`,
        solution_type: problemTypes.MCQ,
        choice: existingSolution?.choice,
      };

      yield call(prepareSolutionAndSave, {
        problemSlug,
        solutionSlug: existingSolution?.slug || null,
        solution: solutionPayload,
        isAutoSave: false,
      });
    }

    // Always call fetch_next_problem with problem_id=current_problem_id
    const nextProblemResp = yield call(
      fetchNextProblemApi,
      testSolutionSet.id,
      currentProblemId
    );

    const data = nextProblemResp?.data;
    let nextProblemId = null;
    let nextProblemSlug = '';

    if (data && typeof data === 'object') {
      const problemObj =
        data?.problem && typeof data.problem === 'object' ? data.problem : {};
      nextProblemId = problemObj?.id || null;
      nextProblemSlug = problemObj?.slug || '';
    }

    // If we're on the last problem of the current section, we should not auto-open
    // the next problem (even if backend returns it). Submit section and go back to
    // All Problems page.
    const sectionTotal = getSectionTotalProblems(currentTimedSection);
    const sectionProblemIds = Array.isArray(currentTimedSection?.problem_ids)
      ? currentTimedSection.problem_ids
      : [];
    const isOnLastProblem =
      sectionTotal > 0 &&
      sectionProblemIds.length >= sectionTotal &&
      sectionProblemIds.length > 0 &&
      String(sectionProblemIds[sectionProblemIds.length - 1]) ===
        String(currentProblemId);

    if (isOnLastProblem) {
      const submittedCount = solutionSetSections.filter(
        s => s?.status === 'SUB'
      ).length;
      const isLastSection = solutionSetSections.length - submittedCount === 1;

      if (!isLastSection) {
        const sectionSlug =
          test?.problem_section_map?.[problemSlug] || currentTimedSection?.slug;
        if (sectionSlug && testSolutionSet?.id) {
          yield put({
            type: ActionTypes.SUBMIT_SECTION,
            solutionsetId: testSolutionSet.id,
            sectionSlug,
            forceSubmission: false,
            redirectToProblems: true,
          });
        }
        return;
      }

      yield put(replace(`/test/${test.slug}/problems`));
      return;
    }

    // If backend didn't return a next problem, submit section/test based on sections status
    if (!nextProblemId || !nextProblemSlug) {
      const shownCount = getSectionShownCount(currentTimedSection);
      const isSectionComplete = sectionTotal > 0 && shownCount >= sectionTotal;

      if (!isSectionComplete) {
        yield put(
          showErrorToast('Unable to fetch next question. Please try again.')
        );
        return;
      }
      // Last section completed: take candidate to All Problems page so they can see
      // the submit UI instead of force-submitting immediately.
      yield put(replace(`/test/${test.slug}/problems`));
      return;
    }

    // Update redux current_problem_id before URL change so Solution page can load by id
    yield put({
      type: ActionTypes.UPDATE_ADAPTIVE_CURRENT_PROBLEM,
      payload: { problemId: nextProblemId, problemSlug: nextProblemSlug },
    });

    yield put(replace(`/test/${test.slug}/problem/${nextProblemSlug}`));
  } catch (e) {
    console.error('[ADAPTIVE_TEST] Error in adaptiveNextProblemSaga', e);
    captureException(e);
  }
}

// For the single-page start flow (no proctoring/AI-interview/LNG/pod-video
// problems), init and start are chained back-to-back instead of waiting for
// the candidate to go through the system-check modal + declaration cards.
function* initializeAndStartTest(args) {
  const { userId, userType, testId, testSlug } = args;
  try {
    yield call(initializeTest, {
      userId,
      userType,
      testId,
      testSlug,
      showChecksModal: false,
      isInfraAllocated: true,
      rethrowOnError: true,
    });
    yield call(startTakingTest, { testId, testSlug, rethrowOnError: true });
    // Mirrors SystemCheckModal's `saveSystemCheckState(true)` so that
    // revisiting the instructions page after the test has started doesn't
    // re-trigger the (unused, in this flow) system-check modal.
    sessionStorage.setItem(SYSTEM_CHECK_SESSION_STORAGE_KEY, true);
    yield put({ type: `${ActionTypes.INIT_AND_START_TEST}_SUCCESS` });
  } catch (e) {
    // initializeTest/startTakingTest already show their own error toast.
    yield put({ type: `${ActionTypes.INIT_AND_START_TEST}_FAILURE` });
  }
}

export default function* loadTestSaga() {
  yield takeLatest(ActionTypes.GET_TEST, getTest);
  yield takeLatest(ActionTypes.INIT_TEST, initializeTest);
  yield takeLatest(ActionTypes.START_TEST, startTakingTest);
  yield takeLatest(ActionTypes.INIT_AND_START_TEST, initializeAndStartTest);
  yield takeEvery(ActionTypes.GET_TEST_SOLUTIONSET, getTestSolutionSet);

  /* 
    Todo: make function name `saveOrUpdateSolution` and `getSolution` specific.
      1. For MCQ problem type it should be `createOrUpdateMCQSolution` and `getMCQSolution`
      2. For Subjective problem type it should be `createOrUpdateSubjectiveSolution` and `getSubjectiveSolution`

    Todo: make action name `SAVE_SOLUTION` and `SAVE_SOLUTION_NO_DEBOUNCE` specific.
      1. Action `SAVE_SOLUTION_NO_DEBOUNCE` is used in MCQ container src/components/ProblemTypes/MultipleChoice.js
      rename to `SAVE_MCQ_SOLUTION`
      2. Action `SAVE_SOLUTION` is used in SUB container
        src/components/Solution/Solution.js rename to `SAVE_SUBJECTIVE_SOLUTION`
    
    Move these to seperate file and abstract the base logic in common saga.
  */

  yield debounce(1000, ActionTypes.SAVE_SOLUTION, saveOrUpdateSolution);
  yield takeEvery(ActionTypes.SAVE_SOLUTION_NO_DEBOUNCE, saveOrUpdateSolution);
  yield takeEvery(
    ActionTypes.SAVE_PLAYGROUND_SOLUTION,
    saveOrUpdatePlaygroundSolution
  );
  yield takeEvery(
    ActionTypes.CREATE_OR_UPDATE_VIDEO_SOLUTION,
    saveOrUpdateVideoSolution
  );
  yield takeEvery(
    ActionTypes.CREATE_OR_UPDATE_AUDIO_SOLUTION,
    saveOrUpdateAudioSolution
  );
  yield takeEvery(ActionTypes.GET_PROBLEM_SOLUTION, getSolution);
  yield takeEvery(ActionTypes.GET_DESMOND_PROBLEM_SOLUTION, getdesmondSolution);
  yield takeEvery(ActionTypes.GET_ALL_REVISIONS, getSolutionRevisions);
  yield takeEvery(ActionTypes.SUBMIT_AND_EVALUATE_FBE, onSubmitAndEvaluateFbe);
  yield takeLatest(ActionTypes.GET_INVITE, getUserInvite);
  yield takeLatest(ActionTypes.GET_BOOKMARKS, getBookmarks);
  yield takeEvery(ActionTypes.RUN_CODE_TEST_CASES_RESULT, testCaseRunWrapper);
  yield takeLatest(ActionTypes.SET_BOOKMARK, setBookmark);
  yield takeLatest(ActionTypes.SET_ATTEMPTS_COUNT, setAttemptsCount);
  yield takeLatest(
    ActionTypes.SET_CLUSTER_ATTEMPTS_COUNT,
    setClusterAttemptsCount
  );
  yield takeLatest(ActionTypes.CONTINUE_TEST, continueTakingTest);
  yield takeLatest(ActionTypes.GET_FEEDBACK, getFeedback);
  yield takeLatest(ActionTypes.UPLOAD_ATTACHMENT_PROB, uploadProblemAttachment);
  yield takeLatest(ActionTypes.CHECK_URL_FOR_IFRAME, checkUrlForIframe);
  yield takeLatest(ActionTypes.CLEAR_SOLUTION, clearSolution);
  yield takeEvery(ActionTypes.ADAPTIVE_NEXT_PROBLEM, adaptiveNextProblemSaga);
}
